import { useEffect, useRef, useState } from "react";

function formatElapsedAsGameTime(elapsed, maxPeriod) {
    let remaining = elapsed;
    for (let period = 1; period <= maxPeriod; period++) {
        const duration = period > 4 ? 300 : 600;
        if (remaining <= duration) {
            const timeLeft = duration - remaining;
            const mins = Math.floor(timeLeft / 60);
            const secs = timeLeft % 60;
            const otIndex = period - 4;
            const label = period <= 4 ? `Q${period}` : (maxPeriod - 4 > 1 ? `TE${otIndex}` : "TE");
            return `${label} ${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
        }
        remaining -= duration;
    }
    const lastOtIndex = maxPeriod - 4;
    const lastLabel = maxPeriod <= 4 ? `Q${maxPeriod}` : (lastOtIndex > 1 ? `TE${lastOtIndex}` : "TE");
    return `${lastLabel} 00:00`;
}

function buildCode(name, fallback) {
    if (!name) return fallback;
    return name.replace(/\s+/g, "").substring(0, 3).toUpperCase() || fallback;
}

// Más ancho de pantalla disponible → más buckets (barras más finas pero con más detalle temporal).
function getMaxBars(viewportWidth) {
    if (viewportWidth < 480) return 16;
    if (viewportWidth < 640) return 22;
    if (viewportWidth < 768) return 30;
    if (viewportWidth < 1024) return 42;
    if (viewportWidth < 1280) return 56;
    return 70;
}

// Agrupa los puntos en buckets de tiempo fijo para no dibujar una barra por cada evento del pbp, conservando el pico (mayor |diff|) de cada bucket en vez de solo el último valor.
function buildBuckets(points, totalDuration, maxBars) {
    if (!points.length || !totalDuration) return [];

    const bucketDuration = totalDuration / maxBars;
    const buckets = [];
    let pointerIndex = 0;

    for (let bucketIndex = 0; bucketIndex < maxBars; bucketIndex++) {
        const bucketStart = bucketIndex * bucketDuration;
        const bucketEnd = bucketIndex * bucketDuration + bucketDuration;

        let bestIndex = pointerIndex;
        let bestAbsDiff = Math.abs(points[pointerIndex]?.diff ?? 0);

        while (pointerIndex < points.length - 1 && points[pointerIndex + 1].elapsed <= bucketEnd) {
            pointerIndex += 1;
            const candidateAbsDiff = Math.abs(points[pointerIndex].diff);
            if (candidateAbsDiff >= bestAbsDiff) {
                bestAbsDiff = candidateAbsDiff;
                bestIndex = pointerIndex;
            }
        }

        buckets.push({
            point: points[bestIndex],
            startElapsed: bucketStart,
            endElapsed: bucketEnd,
        });
    }

    return buckets;
}

function PartidoScoreEvolutionChart({ scoreEvolution, localName, awayName, isHomeAguada, aguadaWon }) {
    const [hoveredPointIndex, setHoveredPointIndex] = useState(null);
    const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1024));
    const isMobile = viewportWidth < 640;
    const accentClass = aguadaWon ? "bg-green-500" : "bg-red-500";
    const points = scoreEvolution?.points || [];
    const chartContainerRef = useRef(null);

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }

        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };

        handleResize();
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const maxBars = getMaxBars(viewportWidth);

    // La cantidad de buckets cambia con el ancho; invalidar la selección para no apuntar a un índice de otro tamaño.
    useEffect(() => {
        setHoveredPointIndex(null);
    }, [maxBars]);

    // Deselecciona la barra activa al hacer click/tap fuera del gráfico.
    useEffect(() => {
        if (hoveredPointIndex === null || typeof document === "undefined") {
            return undefined;
        }

        const handleOutsideClick = (event) => {
            if (chartContainerRef.current && !chartContainerRef.current.contains(event.target)) {
                setHoveredPointIndex(null);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
        };
    }, [hoveredPointIndex]);

    if (!points.length) {
        return (
            <section className="flex-1 bg-[#102216] rounded-xl p-3 sm:p-4 md:p-6 border border-white/10">
                <h3 className="text-lg sm:text-2xl font-bold text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
                    <div className={`w-1.5 sm:w-2 h-6 sm:h-8 ${accentClass} rounded-full`}></div>
                    Evolución del Marcador
                </h3>
                <div className="rounded-lg border border-white/10 bg-[#0f1f15] p-6 text-slate-300 text-sm text-center">
                    No hay datos disponibles para mostrar la evolución del marcador.
                </div>
            </section>
        );
    }

    const width = 1000;
    const height = isMobile ? 620 : 420;
    const paddingLeft = isMobile ? 24 : 32;
    const paddingRight = isMobile ? 24 : 32;
    const paddingTop = isMobile ? 22 : 26;
    const paddingBottom = isMobile ? 72 : 58;
    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;
    const totalDuration = scoreEvolution.totalDuration || 1;
    const maxAbsDiff = Math.max(scoreEvolution.maxAbsDiff || 0, 1);

    const mapX = (elapsed) => paddingLeft + ((elapsed / totalDuration) * chartWidth);
    const mapY = (diff) => {
        const normalized = (diff + maxAbsDiff) / (2 * maxAbsDiff);
        return paddingTop + (chartHeight - (normalized * chartHeight));
    };

    const localColor = aguadaWon ? "#22c55e" : "#f87171";
    const awayColor = !aguadaWon ? "#22c55e" : "#f87171";
    const localDisplayName = localName || "Local";
    const awayDisplayName = awayName || "Visitante";
    const localCode = buildCode(localDisplayName, "LOC");
    const awayCode = buildCode(awayDisplayName, "VIS");

    const mapDiff = (item) => {
        const rawDiff = Number.isFinite(Number(item?.diff)) ? Number(item.diff) : (Number(item?.aguada) - Number(item?.rival));
        return isHomeAguada ? rawDiff : -rawDiff;
    };

    const mapLocalScore = (item) => (isHomeAguada ? item.aguada : item.rival);
    const mapAwayScore = (item) => (isHomeAguada ? item.rival : item.aguada);
    const zeroY = mapY(0);

    const yTickValues = [-maxAbsDiff, -(maxAbsDiff / 2), 0, maxAbsDiff / 2, maxAbsDiff];
    const yTicks = yTickValues.map((value, index) => ({
        key: `y-${index}`,
        value,
        y: mapY(value),
    }));

    const xTicks = (scoreEvolution.labels || []).map((label) => ({
        ...label,
        x: mapX(Math.min(label.elapsed, totalDuration)),
    }));

    const buckets = buildBuckets(points, totalDuration, maxBars);
    const hoveredPoint = hoveredPointIndex === null ? null : (buckets[hoveredPointIndex]?.point || null);

    const tickFontSize = 20;
    const codeFontSize = 28;

    const handleBarTap = (index) => {
        setHoveredPointIndex((current) => (current === index ? null : index));
    };

    const tooltipX = hoveredPoint ? mapX(hoveredPoint.elapsed) : null;
    const tooltipLocalScore = hoveredPoint ? mapLocalScore(hoveredPoint) : null;
    const tooltipAwayScore = hoveredPoint ? mapAwayScore(hoveredPoint) : null;
    const tooltipDiff = hoveredPoint ? mapDiff(hoveredPoint) : null;
    const tooltipLeadY = hoveredPoint ? mapY(tooltipDiff) : null;
    const tooltipLeadColor = tooltipDiff >= 0 ? localColor : awayColor;
    const tooltipLeadCode = tooltipDiff >= 0 ? localCode : awayCode;
    const tooltipGameTime = hoveredPoint ? formatElapsedAsGameTime(hoveredPoint.elapsed, scoreEvolution.maxPeriod || 4) : null;

    const bars = buckets.map((bucket, index) => {
        const value = mapDiff(bucket.point);
        const xStart = mapX(bucket.startElapsed);
        const xEnd = mapX(Math.min(bucket.endElapsed, totalDuration));
        const widthValue = Math.max(xEnd - xStart, 1.5);
        const yValue = value >= 0 ? mapY(value) : zeroY;
        const heightValue = Math.max(Math.abs(mapY(value) - zeroY), 1.5);

        return {
            key: `bar-${index}`,
            index,
            x: xStart,
            y: yValue,
            width: widthValue,
            height: heightValue,
            fill: value >= 0 ? localColor : awayColor,
            tapX: xStart,
            tapWidth: widthValue,
        };
    });

    return (
        <section className="flex-1 bg-[#102216] rounded-xl p-3 sm:p-4 md:p-6 border border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
                <h3 className="text-lg sm:text-2xl font-bold text-slate-100 flex items-center gap-2">
                    <div className={`w-1.5 sm:w-2 h-6 sm:h-8 ${accentClass} rounded-full`}></div>
                    Evolución del Marcador (Diferencia)
                </h3>

                <div className="flex items-center gap-3 sm:gap-5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-slate-300">
                    <span className="flex items-center gap-1.5 sm:gap-2">
                        <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full" style={{ backgroundColor: localColor }} />
                        {localDisplayName.toUpperCase()}
                    </span>
                    <span className="flex items-center gap-1.5 sm:gap-2">
                        <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full" style={{ backgroundColor: awayColor }} />
                        {awayDisplayName.toUpperCase()}
                    </span>
                </div>
            </div>

            <div ref={chartContainerRef} className="rounded-lg border border-white/10 bg-[#0f1f15] p-1.5 sm:p-3 md:p-4">
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="Evolución del marcador de Aguada y rival">
                    <rect
                        x={paddingLeft}
                        y={paddingTop}
                        width={chartWidth}
                        height={chartHeight}
                        fill="none"
                        stroke="rgba(255,255,255,0.08)"
                    />

                    {yTicks.map((tick) => (
                        <g key={tick.key}>
                            <line
                                x1={paddingLeft}
                                y1={tick.y}
                                x2={paddingLeft + chartWidth}
                                y2={tick.y}
                                stroke={Math.abs(tick.value) < 0.01 ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.12)"}
                                strokeWidth={Math.abs(tick.value) < 0.01 ? 2 : 1}
                                strokeDasharray={Math.abs(tick.value) < 0.01 ? "" : "4 6"}
                            />
                            <text
                                x={paddingLeft + 6}
                                y={tick.y - 4}
                                fill="rgba(255,255,255,0.55)"
                                fontSize={tickFontSize}
                                fontWeight="700"
                            >
                                {Math.round(Math.abs(tick.value))}
                            </text>
                        </g>
                    ))}

                    {xTicks.map((tick) => (
                        <line
                            key={`line-${tick.key}`}
                            x1={tick.x}
                            y1={paddingTop}
                            x2={tick.x}
                            y2={paddingTop + chartHeight}
                            stroke="rgba(255,255,255,0.06)"
                        />
                    ))}

                    {bars.map((bar) => (
                        <rect
                            key={bar.key}
                            x={bar.x}
                            y={bar.y}
                            width={bar.width}
                            height={bar.height}
                            fill={bar.fill}
                            opacity={hoveredPointIndex !== null && hoveredPointIndex !== bar.index ? 0.4 : 0.95}
                        />
                    ))}

                    {bars.map((bar) => (
                        <rect
                            key={`tap-${bar.key}`}
                            x={bar.tapX}
                            y={paddingTop}
                            width={bar.tapWidth}
                            height={chartHeight}
                            fill="transparent"
                            onClick={() => handleBarTap(bar.index)}
                        />
                    ))}

                    <text
                        x={paddingLeft + chartWidth - 4}
                        y={paddingTop + 18}
                        fill={localColor}
                        fontSize={codeFontSize}
                        fontWeight="800"
                        textAnchor="end"
                    >
                        {localCode}
                    </text>

                    <text
                        x={paddingLeft + chartWidth - 4}
                        y={paddingTop + chartHeight - 8}
                        fill={awayColor}
                        fontSize={codeFontSize}
                        fontWeight="800"
                        textAnchor="end"
                    >
                        {awayCode}
                    </text>

                    {hoveredPoint ? (
                        <>
                            <line
                                x1={tooltipX}
                                y1={paddingTop}
                                x2={tooltipX}
                                y2={paddingTop + chartHeight}
                                stroke="rgba(255,255,255,0.25)"
                                strokeDasharray="6 6"
                            />

                            <circle cx={tooltipX} cy={tooltipLeadY} r="7" fill={tooltipLeadColor} stroke="#ffffff" strokeWidth="2" />
                        </>
                    ) : null}
                </svg>

                <div className="grid gap-2 text-[11px] sm:text-xs font-bold uppercase tracking-[0.12em] text-slate-500 mt-3 px-1" style={{ gridTemplateColumns: `repeat(${xTicks.length}, minmax(0, 1fr))` }}>
                    {xTicks.map((tick) => (
                        <span key={`label-${tick.key}`} className="text-center">{tick.text}</span>
                    ))}
                </div>

                <div className="mt-3 rounded-lg border border-white/10 bg-[#0a1a11] p-3 text-sm">
                    {hoveredPoint ? (
                        <div className="space-y-1">
                            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{tooltipGameTime}</p>
                            <p className="font-bold" style={{ color: tooltipLeadColor }}>
                                Diferencia: {tooltipDiff > 0 ? "+" : ""}{tooltipDiff} {tooltipLeadCode}
                            </p>
                            <p className="text-slate-200 font-semibold">
                                {localDisplayName}: {tooltipLocalScore} | {awayDisplayName}: {tooltipAwayScore}
                            </p>
                        </div>
                    ) : (
                        <p className="text-center text-slate-400">Tocá una barra del gráfico para ver el detalle.</p>
                    )}
                </div>
            </div>
        </section>
    );
}

export default PartidoScoreEvolutionChart;
